import axios from 'axios';
import { loadItem, saveItem, removeItem } from '@/services/Storage';
import router from '@/router';
const sha512 = require('sha512');

// Axios Setup
// Instance for cloud Rest API
const cloudAxios = () => {
    const a = axios.create({
        headers: { authorization: loadItem('authorization' || '') }
    });

    a.interceptors.response.use(
        response => response,
        error => {
            if (error.response) {
                if (
                    401 === error.response.status ||
                    403 === error.response.status
                ) {
                    removeItem('authToken');
                    removeItem('authorization');
                    router.replace('login');
                } else {
                    return Promise.reject(error);
                }
            } else {
                return Promise.reject({
                    type: 'SERVER_NOT_FOUND',
                    message: 'Server nicht bekannt'
                });
            }
        }
    );

    return a;
};

// Instance for common Rest API
const commonAxios = () => {
    const a = axios.create({
        headers: {
            'Content-Type': ' application/json',
            'X-Version': '2',
            'authToken': loadItem('authToken') || ''
        }
    });
    a.interceptors.response.use(
        response => response,
        error => {
            if (error.response) {
                if (
                    401 === error.response.status ||
                    403 === error.response.status
                ) {
                    removeItem('authToken');
                    removeItem('authorization');
                    router.replace('login');
                } else {
                    return Promise.reject(error);
                }
            } else {
                return Promise.reject({
                    type: 'SERVER_NOT_FOUND',
                    message: 'Server nicht bekannt'
                });
            }
        }
    );

    return a;
};

// xml-rpc server endpoint generator
const xmlRpcEndpoint = () => {
    return 'https://' + loadItem('serverUrl') + '/xml-rpc?de.vertico.starface.auth=' + loadItem('xmlRpcToken');
};

const isLoggedIn = () => {
    return !!loadItem('authToken');
};

const logout = () => {
    removeItem('authToken');
    removeItem ('loginId');
    removeItem('authorization');
    removeItem('xmlRpcToken');
    removeItem('serverUrl');
    removeItem('serverSubdomain');
    removeItem('xmlRpcGenerator');
};

const getServerSubdomain = () => {
   return loadItem('serverSubdomain');
};

const createXmlRpcToken = (loginId: string, password: string) => {
    if (password) {
        saveItem('xmlRpcGenerator', sha512(password).toString('hex'), false);
    }
    const xmlRpcToken = loginId + ':' + sha512(loginId + '*' + loadItem('xmlRpcGenerator')).toString('hex');
    saveItem('xmlRpcToken', xmlRpcToken, false);
 };

// Testing creds exxetatest.starface-cloud.com:4972150993843:exxetaStarfaceUp
const authenticate = async (
    serverUrl: string,
    loginId: string,
    password: string,
    rememberMe: boolean
) => {
    saveItem('serverSubdomain', serverUrl, rememberMe);

    const fullServerUrl = serverUrl + '.starface-cloud.com';
    const urlLogin = 'https://' + fullServerUrl + '/rest/login';

    // Generating and saving xml-rpc server auth token
    createXmlRpcToken(loginId, password);
    return commonAxios()
        .get(urlLogin)
        .then(response => {
            if (response.status === 200) {
                const nonceResponse = response.data.nonce;
                const login =
                    loginId + nonceResponse + sha512(password).toString('hex');
                const shaLogin = sha512(login).toString('hex');
                const finalLogin = loginId + ':' + shaLogin;
                const postBody = {
                    loginType: 'Internal',
                    nonce: nonceResponse,
                    secret: finalLogin
                };

                const promises = Promise.all([
                    commonAxios().request({
                        url: urlLogin,
                        method: 'post',
                        data: postBody
                    }),
                    authenticateToCloud(serverUrl, loginId, password),
                ]);

                return promises
                    .then(responses => {
                        if (responses.every(resp => resp.status === 200 && !resp.data.hasOwnProperty('error'))) {
                            const authToken = responses[0].data.token || '';
                            const authorizationToken =
                                responses[1].data.loginCredential || '';
                            saveItem('authToken', authToken, rememberMe);
                            saveItem('serverUrl', fullServerUrl, rememberMe);
                            saveItem('loginId', loginId, false);
                            saveItem(
                                'authorization',
                                authorizationToken,
                                rememberMe
                            );

                            return {
                                authSuccess:
                                    !!authToken && !!authorizationToken,
                                token: authToken,
                                message: ''
                            };
                        } else {
                            const errMsg = responses[1].data.error ?
                                { message: 'Das ist kein STARFACE UP Login.'} :
                                responses[0].data.error;
                            return {
                                authSuccess: false,
                                error: errMsg
                            };
                        }
                    })
                    .catch(err => {
                        return {
                            authSuccess: false,
                            message: err.response.data
                        };
                    });
            } else {
                return {
                    authSuccess: false,
                    error:
                        'Error occured: Status ' +
                        response.status +
                        ' was returned!'
                };
            }
        });
};

const authenticateToCloud = (
    subdomain: string,
    loginId: string,
    passwort: string
) => {
    const cloudUrl = 'https://cc-api.starface.de/starfacecc/authcloud';
    const body = {
        loginId,
        passwort,
        subdomain
    };

    return cloudAxios().request({
        url: cloudUrl,
        method: 'post',
        data: body
    });
};

export { authenticate, isLoggedIn, cloudAxios, commonAxios, xmlRpcEndpoint, logout , getServerSubdomain, createXmlRpcToken};
